import { useEffect, useMemo, useState } from 'react';
import { X, ChevronRight, ChevronDown, Lock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  useBrowseCategories,
  type BrowseCategoryNode,
} from '../hooks/useBrowseCategories';

interface CatalogFiltersShape {
  browse_category_id: string | null;
  item_types: string[] | null;
  trade_id: string | null;
  classification_category_id: string | null;
  include_system: boolean;
}

interface CatalogLeftPanelProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  filters: CatalogFiltersShape;
  onUpdateFilter: <K extends keyof CatalogFiltersShape>(
    key: K,
    value: CatalogFiltersShape[K]
  ) => void;
  onResetFilters: () => void;
  showSystemItemsCompanySetting: boolean;
}

const ITEM_TYPE_OPTIONS = [
  { id: 'internal', label: 'Internal Items' },
  { id: 'supplier', label: 'Supplier Items' },
  { id: 'assembly', label: 'Assemblies' },
  { id: 'system', label: 'System Items' },
] as const;

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
    {children}
  </div>
);

interface CategoryRowProps {
  node: BrowseCategoryNode;
  depth: number;
  selectedId: string | null;
  expanded: Set<string>;
  onToggleExpand: (id: string) => void;
  onSelect: (id: string) => void;
}

function CategoryRow({
  node,
  depth,
  selectedId,
  expanded,
  onToggleExpand,
  onSelect,
}: CategoryRowProps) {
  const isSelected = selectedId === node.id;
  const isExpanded = expanded.has(node.id);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <div
        className={cn(
          'flex items-center text-sm py-1 px-2 rounded cursor-pointer hover:bg-muted transition-colors',
          isSelected && 'bg-primary/10 text-primary font-medium'
        )}
        style={{ paddingLeft: 8 + depth * 12 }}
        onClick={() => onSelect(node.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            className="mr-1 p-0.5 hover:bg-muted rounded"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="w-4 mr-1" />
        )}
        <span className="truncate">{node.name}</span>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <CategoryRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CatalogLeftPanel({
  isOpen,
  onClose,
  companyId,
  filters,
  onUpdateFilter,
  onResetFilters,
  showSystemItemsCompanySetting,
}: CatalogLeftPanelProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { tree, flat, isLoading: categoriesLoading } = useBrowseCategories();

  const { data: classifications, isLoading: classificationsLoading } = useQuery({
    queryKey: ['catalog_left_panel_classifications', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_categories')
        .select('id, name')
        .eq('is_default', true)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: trades, isLoading: tradesLoading } = useQuery({
    queryKey: ['catalog_left_panel_trades'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_trades')
        .select('id, name')
        .is('company_id', null)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  // Auto-expand ancestors of selected category
  useEffect(() => {
    if (!filters.browse_category_id || flat.length === 0) return;
    const ancestors = new Set<string>(expanded);
    let current = flat.find((c) => c.id === filters.browse_category_id);
    while (current?.parent_id) {
      ancestors.add(current.parent_id);
      current = flat.find((c) => c.id === current!.parent_id);
    }
    setExpanded(ancestors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.browse_category_id, flat]);

  const hasAnyFilter = useMemo(() => {
    return (
      filters.browse_category_id !== null ||
      (filters.item_types !== null && filters.item_types.length > 0) ||
      filters.trade_id !== null ||
      filters.classification_category_id !== null
    );
  }, [filters]);

  if (!isOpen) return null;

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectCategory = (id: string) => {
    if (filters.browse_category_id === id) {
      onUpdateFilter('browse_category_id', null);
    } else {
      onUpdateFilter('browse_category_id', id);
    }
  };

  const handleItemTypeChange = (typeId: string, checked: boolean) => {
    const current = filters.item_types ?? [];
    let next: string[];
    if (checked) {
      next = current.includes(typeId) ? current : [...current, typeId];
    } else {
      next = current.filter((t) => t !== typeId);
    }
    onUpdateFilter('item_types', next.length === 0 ? null : next);
  };

  const itemTypes = filters.item_types ?? [];

  return (
    <aside className="w-60 shrink-0 border-r bg-background flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="text-sm font-semibold">Filters</div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
          aria-label="Close filters"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {hasAnyFilter && (
        <div className="px-3 py-2 border-b">
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={onResetFilters}
          >
            Clear all
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto pb-20">
        {/* Browse */}
        <div className="p-3 border-b">
          <SectionTitle>Browse</SectionTitle>
          {categoriesLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-6 w-2/3" />
            </div>
          ) : flat.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">
              No categories yet
            </div>
          ) : (
            <div>
              <div
                className={cn(
                  'text-sm py-1 px-2 rounded cursor-pointer hover:bg-muted transition-colors',
                  filters.browse_category_id === null &&
                    'bg-primary/10 text-primary font-medium'
                )}
                onClick={() => onUpdateFilter('browse_category_id', null)}
              >
                All
              </div>
              {tree.map((node) => (
                <CategoryRow
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedId={filters.browse_category_id}
                  expanded={expanded}
                  onToggleExpand={toggleExpand}
                  onSelect={selectCategory}
                />
              ))}
            </div>
          )}
        </div>

        {/* Item Type */}
        <div className="p-3 border-b">
          <SectionTitle>Item Type</SectionTitle>
          <div className="space-y-2">
            {ITEM_TYPE_OPTIONS.map((opt) => {
              if (opt.id === 'system' && !showSystemItemsCompanySetting) {
                return null;
              }
              const checked = itemTypes.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) =>
                      handleItemTypeChange(opt.id, v === true)
                    }
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
            {!showSystemItemsCompanySetting && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground italic pt-1">
                <Lock className="h-3 w-3 mt-0.5 shrink-0" />
                <span>System items hidden by your administrator</span>
              </div>
            )}
          </div>
        </div>

        {/* Classification */}
        <div className="p-3 border-b">
          <SectionTitle>Classification</SectionTitle>
          {classificationsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          ) : (
            <RadioGroup
              value={filters.classification_category_id ?? '__all__'}
              onValueChange={(v) =>
                onUpdateFilter(
                  'classification_category_id',
                  v === '__all__' ? null : v
                )
              }
            >
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="__all__" id="class-all" />
                <span>All</span>
              </label>
              {(classifications ?? []).map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <RadioGroupItem value={c.id} id={`class-${c.id}`} />
                  <span className="truncate">{c.name}</span>
                </label>
              ))}
            </RadioGroup>
          )}
        </div>

        {/* Trade */}
        <div className="p-3">
          <SectionTitle>Trade</SectionTitle>
          {tradesLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          ) : (
            <div className="pr-1">
              <RadioGroup
                value={filters.trade_id ?? '__all__'}
                onValueChange={(v) =>
                  onUpdateFilter('trade_id', v === '__all__' ? null : v)
                }
              >
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="__all__" id="trade-all" />
                  <span>All</span>
                </label>
                {(trades ?? []).map((t) => (
                  <label
                    key={t.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <RadioGroupItem value={t.id} id={`trade-${t.id}`} />
                    <span className="truncate">{t.name}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

export default CatalogLeftPanel;
